import React from "react";
import i18next from "src/i18n/config";

import { showErrorToast } from "@shared/toasts/Toasts";

import type { GroupDropState } from "../../groupDrag.utils";

import type * as GroupDragAdapter from "./groupDragAdapter";

let adapter: typeof GroupDragAdapter | null = null;
let adapterRequest: Promise<void> | null = null;
const adapterListeners = new Set<() => void>();

const loadAdapter = () => {
    adapterRequest ??= import("./groupDragAdapter").then(
        (module) => {
            adapter = module;
            adapterListeners.forEach((listener) => listener());
        },
        () => {
            adapterRequest = null;
            showErrorToast(i18next.t("common:error.failedToLoadFeature"));
        },
    );
};

const subscribeToAdapter = (listener: () => void) => {
    adapterListeners.add(listener);
    return () => {
        adapterListeners.delete(listener);
    };
};

const useAdapter = () => React.useSyncExternalStore(subscribeToAdapter, () => adapter);

type GroupDragContextValue = {
    canDrag: (groupId: number) => boolean;
    getDropState: (draggedId: number, targetId: number | null) => GroupDropState;
    expandGroup: (groupId: number) => void;
};

/**
 * Drop rules and callbacks the groups page hands to its rows; without a provider no row can be dragged.
 */
export const GroupDragContext = React.createContext<GroupDragContextValue>({
    canDrag: () => false,
    getDropState: () => "notAllowed",
    expandGroup: () => {},
});

/**
 * Makes a group row draggable by its grip and a drop target as a whole. A collapsed row opens while a group hovers it.
 */
export const useGroupRowDrag = (groupId: number, isCollapsed: boolean) => {
    const { canDrag, getDropState, expandGroup } = React.useContext(GroupDragContext);
    const rowRef = React.useRef<HTMLTableRowElement | null>(null);
    const handleRef = React.useRef<HTMLSpanElement | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [hoveredBy, setHoveredBy] = React.useState<number | null>(null);
    const draggable = canDrag(groupId);
    const loadedAdapter = useAdapter();
    const onHoverExpand = React.useEffectEvent(() => {
        if (isCollapsed) {
            expandGroup(groupId);
        }
    });

    React.useEffect(() => {
        const row = rowRef.current;
        if (!loadedAdapter || !row) {
            return;
        }

        return loadedAdapter.registerGroupRow({
            groupId,
            row,
            handle: draggable ? handleRef.current : null,
            onDraggingChange: setIsDragging,
            onHoveredByChange: setHoveredBy,
            onHoverExpand: () => onHoverExpand(),
        });
    }, [loadedAdapter, groupId, draggable]);

    return {
        rowRef,
        handleRef: draggable ? handleRef : null,
        onHandlePointerEnter: loadAdapter,
        isDragging,
        // The dragged row is under the pointer as soon as the drag starts; marking it as refused would only be noise.
        dropState: hoveredBy === null || hoveredBy === groupId ? null : getDropState(hoveredBy, groupId),
    };
};

/**
 * Reports which group is being dragged and where it was dropped, and scrolls the table near its edges.
 */
export const useGroupDropZone = (
    scrollContainer: HTMLElement | null,
    onDrop: (draggedId: number, targetId: number | null) => void,
) => {
    const loadedAdapter = useAdapter();
    const [draggedGroupId, setDraggedGroupId] = React.useState<number | null>(null);
    const handleDrop = React.useEffectEvent(onDrop);

    React.useEffect(() => {
        if (!loadedAdapter || !scrollContainer) {
            return;
        }

        return loadedAdapter.registerGroupDropZone({
            scrollContainer,
            onDraggedGroupChange: setDraggedGroupId,
            onDrop: (draggedId, targetId) => handleDrop(draggedId, targetId),
        });
    }, [loadedAdapter, scrollContainer]);

    return draggedGroupId;
};
export const useTopLevelDropTarget = (element: HTMLElement | null) => {
    const loadedAdapter = useAdapter();
    const [isActive, setIsActive] = React.useState(false);

    React.useEffect(() => {
        if (!loadedAdapter || !element) {
            return;
        }

        return loadedAdapter.registerTopLevelDropTarget(element, setIsActive);
    }, [loadedAdapter, element]);

    return isActive;
};
